<script setup lang="ts">
import { ref } from 'vue'

import type { Component } from 'vue'

/**
 * Segmented control / inline tab switcher: a row of mutually-exclusive options with a white active
 * "thumb" on a subtle track. Two variants of the same shape:
 *  - `tabs`    — full WAI-ARIA tablist (role=tablist/tab, roving arrow-key focus, optional badges),
 *                emerald active text. Used for the route-detail Details/Profile/Waypoints tabs.
 *  - `compact` — a lower-weight value picker (aria-pressed buttons, optional leading label), gray
 *                active text. Used for the "Color by" gradient/surface/way-type selector.
 *
 * v-model'd to the active option value. For the `tabs` variant pass `optionIdPrefix`/`panelIdPrefix`
 * to wire `id` + `aria-controls` to the panels the consumer renders.
 */
export interface SegmentedOption {
  value: string | number
  label: string
  icon?: Component
  /** Optional trailing count pill (tabs variant). Null/undefined hides it. */
  badge?: string | number | null
  testId?: string
  disabled?: boolean
}
interface Props {
  modelValue: string | number
  options: SegmentedOption[]
  variant?: 'tabs' | 'compact'
  /** tablist aria-label (tabs variant). */
  ariaLabel?: string
  /** Leading uppercase label (compact variant), e.g. "Color by". */
  label?: string
  /** Disable the whole control. */
  disabled?: boolean
  /** tabs variant: button id = `${optionIdPrefix}-${value}`. */
  optionIdPrefix?: string
  /** tabs variant: aria-controls = `${panelIdPrefix}-${value}`. */
  panelIdPrefix?: string
}
const props = withDefaults(defineProps<Props>(), { variant: 'tabs', disabled: false })
const emit = defineEmits<{ 'update:modelValue': [value: string | number] }>()

const buttonEls = ref<HTMLButtonElement[]>([])

const select = (option: SegmentedOption) => {
  if (props.disabled || option.disabled || option.value === props.modelValue) return
  emit('update:modelValue', option.value)
}

// Roving arrow-key navigation across the tablist (tabs variant).
const onKeydown = (event: KeyboardEvent) => {
  if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
  event.preventDefault()
  const index = props.options.findIndex((o) => o.value === props.modelValue)
  if (index === -1) return
  const delta = event.key === 'ArrowRight' ? 1 : -1
  const next = props.options[(index + delta + props.options.length) % props.options.length]
  select(next)
  void Promise.resolve().then(() => {
    const nextIndex = props.options.findIndex((o) => o.value === next.value)
    buttonEls.value[nextIndex]?.focus()
  })
}
</script>

<template>
  <!-- tabs variant -->
  <div
    v-if="props.variant === 'tabs'"
    class="flex items-center gap-1 rounded-xl bg-black/4 p-1"
    role="tablist"
    :aria-label="props.ariaLabel"
    @keydown="onKeydown"
  >
    <button
      v-for="option in props.options"
      :id="props.optionIdPrefix ? `${props.optionIdPrefix}-${option.value}` : undefined"
      :key="option.value"
      ref="buttonEls"
      type="button"
      role="tab"
      :aria-selected="props.modelValue === option.value"
      :aria-controls="props.panelIdPrefix ? `${props.panelIdPrefix}-${option.value}` : undefined"
      :tabindex="props.modelValue === option.value ? 0 : -1"
      :disabled="props.disabled || option.disabled"
      class="flex min-h-9 flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg px-2 py-1.5 text-sm font-medium transition-colors hover:cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
      :class="
        props.modelValue === option.value
          ? 'bg-white text-emerald-700 shadow-sm ring-1 ring-black/5'
          : 'text-neutral-600 hover:text-neutral-900'
      "
      :data-testid="option.testId"
      @pointerdown.stop
      @click.stop="select(option)"
    >
      <component :is="option.icon" v-if="option.icon" class="h-4 w-4 shrink-0" aria-hidden="true" />
      <span>{{ option.label }}</span>
      <span
        v-if="option.badge !== null && option.badge !== undefined"
        class="rounded-full bg-black/6 px-1.5 text-xs tabular-nums text-neutral-600"
        >{{ option.badge }}</span
      >
    </button>
  </div>

  <!-- compact variant -->
  <div
    v-else
    class="flex w-full items-center gap-1 rounded-lg border border-gray-200 bg-gray-50 p-1"
    role="group"
    :aria-label="props.ariaLabel"
  >
    <span
      v-if="props.label"
      class="px-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500"
      >{{ props.label }}</span
    >
    <button
      v-for="option in props.options"
      :key="option.value"
      type="button"
      :disabled="props.disabled || option.disabled"
      :aria-pressed="props.modelValue === option.value"
      class="inline-flex min-w-0 flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-md px-2 py-1 text-xs font-medium transition-colors hover:cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
      :class="
        props.modelValue === option.value
          ? 'bg-white text-gray-900 shadow-sm ring-1 ring-gray-200'
          : 'text-gray-600 hover:bg-white/80 hover:text-gray-900'
      "
      :data-testid="option.testId"
      @click="select(option)"
    >
      <component
        :is="option.icon"
        v-if="option.icon"
        class="h-3.5 w-3.5 shrink-0"
        aria-hidden="true"
      />
      {{ option.label }}
    </button>
  </div>
</template>
